import React, { useState } from 'react';

interface Episode {
  id: string;
  title: string;
  segmentCount: number;
  duration: string;
  speakers: string[];
  description: string;
}

const MOCK_EPISODES: Episode[] = [
  { id: '1', title: 'Morning Rush', segmentCount: 19, duration: '0:55', speakers: ['Amy'], description: 'Amy describes her busy morning routine — waking up, getting ready, and rushing to school.' },
  { id: '2', title: 'School Day', segmentCount: 26, duration: '1:14', speakers: ['Amy', 'Ben'], description: 'Amy and Ben talk about their school schedules and favorite subjects.' },
  { id: '3', title: 'After School', segmentCount: 17, duration: '0:49', speakers: ['Amy'], description: 'Amy shares what she does after school — homework, hobbies, and dinner.' },
  { id: '4', title: 'Comparing Routines', segmentCount: 30, duration: '1:21', speakers: ['Amy', 'Ben'], description: 'Amy and Ben compare their daily routines and discover differences.' },
  { id: '5', title: 'My Ideal Routine', segmentCount: 14, duration: '0:51', speakers: ['Amy'], description: 'Amy imagines her perfect daily routine for the weekend.' },
];

// ==========================================
// Style A: Tabs
// ==========================================
const TabsSelector: React.FC<{ selected: string; onSelect: (id: string) => void }> = ({ selected, onSelect }) => (
  <div style={{ marginBottom: 24 }}>
    <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#374151' }}>Style A — Tabs</h3>
    <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', overflowX: 'auto' }}>
      {MOCK_EPISODES.map((ep, i) => {
        const isActive = ep.id === selected;
        return (
          <button
            key={ep.id}
            onClick={() => onSelect(ep.id)}
            style={{
              padding: '10px 18px',
              border: 'none',
              borderBottom: isActive ? '3px solid #4f46e5' : '3px solid transparent',
              background: isActive ? '#eef2ff' : 'transparent',
              color: isActive ? '#4f46e5' : '#6b7280',
              fontWeight: isActive ? 600 : 400,
              fontSize: 14,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <span style={{ marginRight: 6, fontSize: 12, opacity: 0.7 }}>E{i + 1}</span>
            {ep.title}
            <span style={{
              marginLeft: 8,
              fontSize: 11,
              background: isActive ? '#4f46e5' : '#e5e7eb',
              color: isActive ? 'white' : '#6b7280',
              padding: '2px 6px',
              borderRadius: 10,
            }}>
              {ep.segmentCount}
            </span>
          </button>
        );
      })}
    </div>
    {/* Content preview */}
    {(() => {
      const ep = MOCK_EPISODES.find(e => e.id === selected)!;
      return (
        <div style={{ padding: '16px 20px', background: '#f9fafb', borderRadius: '0 0 12px 12px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#111827' }}>{ep.title}</div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>{ep.description}</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
            <span>🕐 {ep.duration}</span>
            <span>📝 {ep.segmentCount} sentences</span>
            <span>🎙️ {ep.speakers.join(', ')}</span>
          </div>
        </div>
      );
    })()}
  </div>
);

// ==========================================
// Style B: Dropdown
// ==========================================
const DropdownSelector: React.FC<{ selected: string; onSelect: (id: string) => void }> = ({ selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const selectedEp = MOCK_EPISODES.find(e => e.id === selected)!;

  return (
    <div style={{ marginBottom: 24, position: 'relative' }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#374151' }}>Style B — Dropdown</h3>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'white',
          border: '2px solid #d1d5db',
          borderRadius: 12,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 15,
          transition: 'border-color 0.2s',
          borderColor: open ? '#4f46e5' : '#d1d5db',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            background: '#4f46e5',
            color: 'white',
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            fontWeight: 600,
          }}>
            {MOCK_EPISODES.indexOf(selectedEp) + 1}
          </span>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontWeight: 500, color: '#111827' }}>{selectedEp.title}</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>
              {selectedEp.segmentCount} sentences · {selectedEp.duration} · {selectedEp.speakers.join(', ')}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 18, color: '#9ca3af', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: 'white',
          border: '2px solid #e5e7eb',
          borderRadius: 12,
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
          zIndex: 50,
          overflow: 'hidden',
        }}>
          {MOCK_EPISODES.map((ep, i) => {
            const isActive = ep.id === selected;
            return (
              <button
                key={ep.id}
                onClick={() => { onSelect(ep.id); setOpen(false); }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  borderBottom: i < MOCK_EPISODES.length - 1 ? '1px solid #f3f4f6' : 'none',
                  background: isActive ? '#eef2ff' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  transition: 'background 0.15s',
                  textAlign: 'left',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'white'; }}
              >
                <span style={{
                  background: isActive ? '#4f46e5' : '#e5e7eb',
                  color: isActive ? 'white' : '#6b7280',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 600,
                  flexShrink: 0,
                }}>
                  {i + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: isActive ? 600 : 400, color: isActive ? '#4f46e5' : '#111827', fontSize: 14 }}>{ep.title}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    {ep.segmentCount} sentences · {ep.duration}
                  </div>
                </div>
                {isActive && <span style={{ color: '#4f46e5', fontSize: 16 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==========================================
// Style C: Accordion
// ==========================================
const AccordionSelector: React.FC<{ selected: string; onSelect: (id: string) => void }> = ({ selected, onSelect }) => (
  <div style={{ marginBottom: 24 }}>
    <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#374151' }}>Style C — Accordion</h3>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {MOCK_EPISODES.map((ep, i) => {
        const isActive = ep.id === selected;
        return (
          <div
            key={ep.id}
            style={{
              border: isActive ? '2px solid #4f46e5' : '1px solid #e5e7eb',
              borderRadius: 12,
              overflow: 'hidden',
              transition: 'all 0.25s',
              background: 'white',
            }}
          >
            <button
              onClick={() => onSelect(ep.id)}
              style={{
                width: '100%',
                padding: '14px 16px',
                border: 'none',
                background: isActive ? '#eef2ff' : 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                transition: 'background 0.15s',
              }}
            >
              <span style={{
                background: isActive ? '#4f46e5' : '#f3f4f6',
                color: isActive ? 'white' : '#6b7280',
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {i + 1}
              </span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 600, color: isActive ? '#4f46e5' : '#111827', fontSize: 15 }}>{ep.title}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                  {ep.segmentCount} sentences · {ep.duration} · {ep.speakers.join(', ')}
                </div>
              </div>
              <span style={{
                fontSize: 16,
                color: '#9ca3af',
                transform: isActive ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.25s',
              }}>
                ▾
              </span>
            </button>

            {/* Expanded content */}
            <div style={{
              maxHeight: isActive ? 200 : 0,
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
            }}>
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '12px 0', lineHeight: 1.5 }}>{ep.description}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{
                    padding: '8px 20px',
                    background: '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}>
                    Start Learning
                  </button>
                  <button style={{
                    padding: '8px 16px',
                    background: 'white',
                    color: '#4f46e5',
                    border: '1px solid #4f46e5',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}>
                    Preview
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ==========================================
// Style D: Card Grid (bonus)
// ==========================================
const CardGridSelector: React.FC<{ selected: string; onSelect: (id: string) => void }> = ({ selected, onSelect }) => (
  <div style={{ marginBottom: 24 }}>
    <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 600, color: '#374151' }}>Style D — Card Grid (bonus)</h3>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
      {MOCK_EPISODES.map((ep, i) => {
        const isActive = ep.id === selected;
        const colors = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];
        return (
          <button
            key={ep.id}
            onClick={() => onSelect(ep.id)}
            style={{
              padding: 16,
              border: isActive ? `2px solid ${colors[i]}` : '1px solid #e5e7eb',
              borderRadius: 14,
              background: isActive ? `${colors[i]}08` : 'white',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s',
              transform: isActive ? 'scale(1.02)' : 'none',
              boxShadow: isActive ? `0 4px 12px ${colors[i]}20` : 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{
                background: colors[i],
                color: 'white',
                width: 26,
                height: 26,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 13,
                fontWeight: 700,
              }}>
                {i + 1}
              </span>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{ep.title}</span>
            </div>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px', lineHeight: 1.4, minHeight: 34 }}>
              {ep.description.substring(0, 70)}...
            </p>
            <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#9ca3af' }}>
              <span>{ep.duration}</span>
              <span>·</span>
              <span>{ep.segmentCount} sent.</span>
              <span>·</span>
              <span>{ep.speakers.join(', ')}</span>
            </div>
            {/* Progress bar mock */}
            <div style={{ marginTop: 10, height: 4, background: '#f3f4f6', borderRadius: 2 }}>
              <div style={{
                height: '100%',
                width: `${[40, 20, 0, 0, 0][i]}%`,
                background: colors[i],
                borderRadius: 2,
                transition: 'width 0.3s',
              }} />
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

// ==========================================
// Main Test Page
// ==========================================
const EpisodeSelectorTestPage: React.FC = () => {
  const [tabSelected, setTabSelected] = useState('1');
  const [dropdownSelected, setDropdownSelected] = useState('1');
  const [accordionSelected, setAccordionSelected] = useState('1');
  const [cardSelected, setCardSelected] = useState('1');

  return (
    <div style={{ maxWidth: 800, margin: '40px auto', padding: '0 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
        Episode Selector — Style Comparison
      </h1>
      <p style={{ color: '#6b7280', marginBottom: 32, fontSize: 15 }}>
        Course: My Daily Routine · 5 episodes · 106 sentences · 5:29
      </p>

      <TabsSelector selected={tabSelected} onSelect={setTabSelected} />
      <hr style={{ border: 'none', borderTop: '2px dashed #e5e7eb', margin: '32px 0' }} />
      <DropdownSelector selected={dropdownSelected} onSelect={setDropdownSelected} />
      <hr style={{ border: 'none', borderTop: '2px dashed #e5e7eb', margin: '32px 0' }} />
      <AccordionSelector selected={accordionSelected} onSelect={setAccordionSelected} />
      <hr style={{ border: 'none', borderTop: '2px dashed #e5e7eb', margin: '32px 0' }} />
      <CardGridSelector selected={cardSelected} onSelect={setCardSelected} />
    </div>
  );
};

export default EpisodeSelectorTestPage;
